import React, { useEffect, useState, useMemo } from "react";
import { Search, SlidersHorizontal, MapPin } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { TaskCard } from "../../components/shared/TaskCard";
import { studentNav } from "../../components/shared/navConfig";
import { tasks as mockTasks, SKILLS, CITIES } from "../../data/mockData";

const workModes = ["On-site", "Hybrid", "Remote"];

export default function FindTasks() {
  const [q, setQ] = useState("");
  const [selSkills, setSelSkills] = useState([]);
  const [selMode, setSelMode] = useState(null);
  const [maxDistance, setMaxDistance] = useState(20);
  const [minBudget, setMinBudget] = useState(0);
  const [city, setCity] = useState("All");
  const [liveTasks, setLiveTasks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tasks/live");
        // Merge live tasks (normalized shape) with mock task shape
        const normalized = data.map(t => ({
          id: t.task_id,
          title: t.title,
          company: t.company_name,
          category: t.category,
          location: t.address || t.city || "—",
          distance: t.work_mode === "Remote" ? "Remote" : "—",
          skills: t.skills || [],
          budget: t.budget || 0,
          deadline: t.deadline || "—",
          workMode: t.work_mode || "On-site",
          status: "Open",
          posted: "recently",
          applications: 0,
          matchScore: 82,
        }));
        setLiveTasks(normalized);
      } catch (_) { setLiveTasks([]); }
    })();
  }, []);

  const tasks = useMemo(() => (liveTasks.length ? [...liveTasks, ...mockTasks] : mockTasks), [liveTasks]);

  const filtered = useMemo(() => tasks.filter(t => {
    if (q && !t.title.toLowerCase().includes(q.toLowerCase()) && !t.company.toLowerCase().includes(q.toLowerCase())) return false;
    if (selMode && t.workMode !== selMode) return false;
    if (selSkills.length && !selSkills.some(s => t.skills.includes(s))) return false;
    if (minBudget && t.budget < minBudget) return false;
    if (city !== "All" && t.workMode !== "Remote" && !t.location.includes(city)) return false;
    if (t.workMode !== "Remote") {
      const d = parseFloat(t.distance) || 0;
      if (d > maxDistance) return false;
    }
    return true;
  }), [q, selSkills, selMode, maxDistance, minBudget, city, tasks]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  return (
    <DashboardShell nav={studentNav} title="Find tasks" subtitle={`${filtered.length} tasks match your filters`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-full border" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
          <Search size={14} className="text-zinc-500"/>
          <input data-testid="tasks-search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by title, company, skill…" className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-zinc-600"/>
        </div>
        <button className="btn-ghost px-4 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
          <SlidersHorizontal size={14}/> Sort
        </button>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Filters */}
        <aside className="lg:col-span-1 card-surface p-5 h-fit sticky top-24">
          <div className="eyebrow mb-4">Filters</div>

          <Filter label="Education">
            <select data-testid="filter-education" value={selMode?.edu || "All"} onChange={e => { const v = e.target.value; setSelMode(m => ({ ...(m || {}), edu: v === "All" ? undefined : v })); }} className="w-full text-sm px-3 py-2 rounded-md border bg-[#111114] text-white outline-none" style={{ borderColor: "var(--border-strong)" }}>
              <option>All</option><option>Any Graduate</option><option>B.Com</option><option>B.Com Hons</option><option>M.Com</option><option>CA / CS / LLB</option>
            </select>
          </Filter>

          <Filter label="City">
            <select data-testid="filter-city" value={city} onChange={e => setCity(e.target.value)} className="w-full text-sm px-3 py-2 rounded-md border bg-[#111114] text-white outline-none" style={{ borderColor: "var(--border-strong)" }}>
              <option>All</option>{CITIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Filter>

          <Filter label={`Distance · ${maxDistance} km`}>
            <input data-testid="filter-distance" type="range" min={1} max={50} value={maxDistance} onChange={e => setMaxDistance(+e.target.value)} className="w-full accent-blue-500"/>
          </Filter>

          <Filter label="Work mode">
            <div className="flex flex-wrap gap-1.5">
              {workModes.map(m => (
                <button key={m} data-testid={`filter-mode-${m}`} onClick={() => setSelMode(selMode === m ? null : m)}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-200 ${selMode === m ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-[#27272A] text-zinc-400 hover:border-[#3A3A42]"}`}>
                  {m}
                </button>
              ))}
            </div>
          </Filter>

          <Filter label={`Min budget · ₹${minBudget.toLocaleString()}`}>
            <input data-testid="filter-budget" type="range" min={0} max={20000} step={500} value={minBudget} onChange={e => setMinBudget(+e.target.value)} className="w-full accent-blue-500"/>
          </Filter>

          <Filter label="Skills">
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
              {SKILLS.slice(0, 8).map(s => {
                const on = selSkills.includes(s);
                return (
                  <button key={s} data-testid={`filter-skill-${s}`} onClick={() => toggle(selSkills, setSelSkills, s)}
                          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-200 ${on ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-[#27272A] text-zinc-400 hover:border-[#3A3A42]"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </Filter>

          <button data-testid="clear-filters-btn" onClick={() => { setSelSkills([]); setSelMode(null); setMaxDistance(20); setMinBudget(0); setCity("All"); }} className="w-full mt-4 text-xs text-zinc-400 hover:text-white transition-colors duration-200">
            Clear all filters
          </button>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-3 grid md:grid-cols-2 gap-4">
          {filtered.length === 0 ? (
            <div className="md:col-span-2 card-surface p-12 text-center text-sm text-zinc-500">
              <MapPin size={22} className="mx-auto mb-3 text-zinc-600"/>
              No tasks match your filters.
            </div>
          ) : filtered.map(t => <TaskCard key={t.id} task={t} showMatch/>)}
        </div>
      </div>
    </DashboardShell>
  );
}

const Filter = ({ label, children }) => (
  <div className="mb-5">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
    {children}
  </div>
);
